import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { Link, useSearchParams, type LinkProps } from "react-router";
import {
  CalendarPlus, Check, ChevronLeft, ChevronRight, Clapperboard, Copy, ExternalLink, House, Lock, MessagesSquare,
  Pin, Play, SlidersHorizontal, UserRound, X,
} from "lucide-react";
import { useFonts } from "../bits";
import { rootStyle } from "../parts";
import {
  CLOCKS, DENVER, DEFAULT_CLOCK, DEFAULT_ROLE, ME, MY_SUPPLIES, PLANS, ROLES, STATE_LABEL,
  can, clockAt, fmtDay, fmtDur, heroSession, isClock, isRole, liveSession, pinnedNote, projectOf,
  relDay, sessionEnd, timeLabel, viewer, viewerTz, who, ago,
  type Clock, type Note, type Person, type Plan, type Role, type Session, type SessionState, type Thing,
} from "./data";
import "./Portal.css";

/* The club portal shell and UI kit (/blue-2/portal prototype).

   Mobile-first: on phones a compact top bar and a bottom tab bar (Home ·
   Classes · Community · Me); from 900px a labeled left sidebar and a content
   column up to 1200px with an optional sticky right rail. A red LIVE NOW banner
   shows on every screen while a club class is live.

   Prototype review state lives in the URL: ?as=<role>&t=<clock>. usePortal()
   reads it anywhere (no provider needed) and every portal link made with
   PLink / Btn / Row / Card / usePortal().to keeps it. Nothing here posts or
   calls a server; all data is sample data from ./data.

   The chrome is deliberately calm (Figtree, hairlines, teal for "active").
   Brand flourishes (Fraunces, Gochi Hand, tape, polaroids) are opt-in pieces
   for hero cards, makes and empty states: Hand, Tape, Polaroid, Empty. */

/* ═══════════════════════════ Routes & URL state ═══════════════════════════ */

export const PORTAL_BASE = "/blue-2/portal";
/** Portal routes. Pass through `to()` / PLink to keep ?as=&t=. */
export const P = {
  home: PORTAL_BASE,
  classes: `${PORTAL_BASE}/classes`,
  project: (id: string) => `${PORTAL_BASE}/projects/${id}`,
  session: (id: string) => `${PORTAL_BASE}/session/${id}`,
  community: `${PORTAL_BASE}/community`,
  me: `${PORTAL_BASE}/me`,
  join: `${PORTAL_BASE}/join`,
} as const;
export type Tab = "home" | "classes" | "community" | "me";

/** Adds the review params to a portal path (other paths pass through untouched).
    Keeps an existing query and #hash: withParams("/blue-2/portal/join?plan=friend#tiers", "free", "live"). */
export function withParams(path: string, role: Role, clock: Clock) {
  if (!path.startsWith(PORTAL_BASE)) return path;
  const [beforeHash, hash] = path.split("#");
  const [p, q] = beforeHash.split("?");
  const sp = new URLSearchParams(q);
  sp.set("as", role); sp.set("t", clock);
  return `${p}?${sp.toString()}${hash ? `#${hash}` : ""}`;
}

/* The simulated clock runs forward in real time from the chosen instant, so
   countdowns tick. It restarts whenever the clock setting changes. */
let anchor: { clock: Clock; real: number } | null = null;
/** The simulated "now" for a clock setting. */
export function simNow(clock: Clock) {
  if (!anchor || anchor.clock !== clock) anchor = { clock, real: Date.now() };
  return new Date(clockAt(clock).getTime() + (Date.now() - anchor.real));
}

function useParams() {
  const [sp, setSp] = useSearchParams();
  const a = sp.get("as"), t = sp.get("t");
  const role: Role = isRole(a) ? a : DEFAULT_ROLE;
  const clock: Clock = isClock(t) ? t : DEFAULT_CLOCK;
  return { role, clock, setSp };
}

export type PortalState = {
  role: Role; clock: Clock;
  /** Simulated now (static per render; use useNow() for a ticking one). */
  now: Date;
  /** Signed-in person for the role (null for visitors). */
  viewer: ReturnType<typeof viewer>;
  /** Time zone for "your time" (ME.tz; Mountain for Chelsea). */
  tz: string;
  setRole: (r: Role) => void; setClock: (c: Clock) => void;
  /** Portal path → same path with ?as=&t= kept. */
  to: (path: string) => string;
  /** can(role, thing) for the current role. */
  can: (thing: Thing) => boolean;
};

/** Review state + helpers. Works in any component under the router; no provider.
    `const { role, clock, now, to, can } = usePortal();` */
export function usePortal(): PortalState {
  const { role, clock, setSp } = useParams();
  const set = useCallback((k: string, v: string) => setSp(prev => {
    const n = new URLSearchParams(prev); n.set(k, v); return n;
  }, { replace: true, preventScrollReset: true }), [setSp]);
  return useMemo(() => ({
    role, clock, now: simNow(clock), viewer: viewer(role), tz: viewerTz(role),
    setRole: (r: Role) => set("as", r), setClock: (c: Clock) => set("t", c),
    to: (path: string) => withParams(path, role, clock),
    can: (thing: Thing) => can(role, thing),
  }), [role, clock, set]);
}

/** A ticking simulated now. `const now = useNow(1000);` */
export function useNow(tickMs = 1000) {
  const { clock } = useParams();
  const [, force] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => force(n => n + 1), tickMs);
    return () => window.clearInterval(id);
  }, [tickMs]);
  return simNow(clock);
}

/** react-router Link that keeps ?as=&t= on portal paths. Same props as Link, `to` must be a string. */
export function PLink({ to, ...rest }: Omit<LinkProps, "to"> & { to: string }) {
  const { to: keep } = usePortal();
  return <Link to={keep(to)} {...rest} />;
}

/* ═══════════════════════════ Countdown & time ═══════════════════════════ */

export type Countdown = { ms: number; done: boolean; days: number; hours: number; minutes: number; seconds: number;
  /** "4d 8h" · "4h 47m" · "12m 30s" · "now" */
  label: string;
  /** "04:47:12" (hours can exceed 24) */
  clock: string };
/** Ticking countdown to a date, on the simulated clock. `const c = useCountdown(session.start); c.label` */
export function useCountdown(target: Date, tickMs = 1000): Countdown {
  const now = useNow(tickMs);
  const ms = Math.max(0, target.getTime() - now.getTime());
  const sec = Math.floor(ms / 1000);
  const days = Math.floor(sec / 86400), hours = Math.floor((sec % 86400) / 3600), minutes = Math.floor((sec % 3600) / 60), seconds = sec % 60;
  const label = ms === 0 ? "now" : days ? `${days}d ${hours}h` : hours ? `${hours}h ${minutes}m` : `${minutes}m ${seconds}s`;
  const p = (n: number) => String(n).padStart(2, "0");
  return { ms, done: ms === 0, days, hours, minutes, seconds, label, clock: `${p(days * 24 + hours)}:${p(minutes)}:${p(seconds)}` };
}

/** Countdown as boxed segments ("4 h · 47 m · 12 s"). `<CountdownBoxes to={s.start} />` */
export function CountdownBoxes({ to, tone = "plain" }: { to: Date; tone?: "plain" | "onDark" }) {
  const c = useCountdown(to);
  const segs = c.days ? [[c.days, "days"], [c.hours, "hrs"], [c.minutes, "min"]] : [[c.hours, "hrs"], [c.minutes, "min"], [c.seconds, "sec"]];
  return <div className={`pt-count pt-count-${tone}`} role="timer" aria-label={`Starts in ${c.label}`}>
    {segs.map(([n, l]) => <span key={l as string}><b>{String(n).padStart(2, "0")}</b><small>{l}</small></span>)}
  </div>;
}

/** "7 PM MT · 9 PM your time", in the viewer's zone. `withDay` prefixes "Tue, Oct 13 · ";
    `relative` prefixes "Tonight · " / "Thursday · ". */
export function LocalTime({ date, withDay, relative, className = "" }: { date: Date; withDay?: boolean; relative?: boolean; className?: string }) {
  const { tz, now } = usePortal();
  const day = relative ? relDay(date, now, tz) : withDay ? fmtDay(date, tz) : "";
  return <time className={`pt-time ${className}`} dateTime={date.toISOString()}>{day ? `${day} · ` : ""}{timeLabel(date, tz)}</time>;
}

/* ═══════════════════════════ Calendar ═══════════════════════════ */

const icsDate = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const sessionLink = (x: Session) => (typeof window === "undefined" ? P.session(x.id) : `${window.location.origin}${P.session(x.id)}`);
/** ICS text for a session (one VEVENT, UTC times). */
export function icsFor(x: Session) {
  const esc = (t: string) => t.replace(/[\\,;]/g, m => `\\${m}`).replace(/\n/g, "\\n");
  const title = x.kind === "club" ? `Loose Threads Club · ${x.title}` : `Free live: ${x.title}`;
  const url = x.kind === "club" ? sessionLink(x) : x.url ?? "";
  return [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Loose Threads Club//Portal prototype//EN", "CALSCALE:GREGORIAN", "METHOD:PUBLISH",
    "BEGIN:VEVENT", `UID:${x.id}@loosethreads.club`, `DTSTAMP:${icsDate(new Date())}`,
    `DTSTART:${icsDate(x.start)}`, `DTEND:${icsDate(sessionEnd(x))}`,
    `SUMMARY:${esc(title)}`, `DESCRIPTION:${esc(`${x.blurb}\n\nJoin: ${url}`)}`, `URL:${url}`,
    "BEGIN:VALARM", "TRIGGER:-PT15M", "ACTION:DISPLAY", `DESCRIPTION:${esc(title)}`, "END:VALARM",
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
}
/** data: URL of icsFor(session), for <a download>. */
export const icsHref = (x: Session) => `data:text/calendar;charset=utf-8,${encodeURIComponent(icsFor(x))}`;
/** Google Calendar "add event" link. */
export function googleCalHref(x: Session) {
  const q = new URLSearchParams({ action: "TEMPLATE", text: x.kind === "club" ? `Loose Threads Club · ${x.title}` : `Free live: ${x.title}`,
    dates: `${icsDate(x.start)}/${icsDate(sessionEnd(x))}`, details: `${x.blurb}\n\n${x.kind === "club" ? sessionLink(x) : x.url ?? ""}`, ctz: "America/Denver" });
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}

/** "Add to calendar" button with a small menu (Apple/Outlook .ics download, Google).
    `<AddToCalendar session={s} />`, `<AddToCalendar session={s} variant="ghost" size="sm" label="Calendar" />` */
export function AddToCalendar({ session, variant = "secondary", size = "md", label = "Add to calendar", block, onDark }: {
  session: Session; variant?: BtnVariant; size?: BtnSize; label?: string; block?: boolean; onDark?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const off = (e: Event) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("pointerdown", off); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", off); document.removeEventListener("keydown", esc); };
  }, [open]);
  return <div className={`pt-cal${block ? " is-block" : ""}`} ref={ref}>
    <Btn variant={variant} size={size} block={block} onDark={onDark} icon={<CalendarPlus size={18} aria-hidden="true" />} aria-expanded={open} aria-haspopup="menu" onClick={() => setOpen(o => !o)}>{label}</Btn>
    {open && <div className="pt-cal-menu" role="menu">
      <a role="menuitem" href={icsHref(session)} download={`${session.id}.ics`} onClick={() => setOpen(false)}>Apple / Outlook <small>.ics file</small></a>
      <a role="menuitem" href={googleCalHref(session)} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>Google Calendar <ExternalLink size={14} aria-hidden="true" /></a>
    </div>}
  </div>;
}

/* ═══════════════════════════ Buttons, chips, cards, rows ═══════════════════════════ */

export type BtnVariant = "primary" | "secondary" | "ghost" | "live" | "pop" | "quiet";
export type BtnSize = "sm" | "md" | "lg";
type BtnProps = {
  variant?: BtnVariant; size?: BtnSize; block?: boolean;
  /** Use on dark/colored cards: secondary/ghost/quiet switch to cream lines. */
  onDark?: boolean;
  /** Internal path (portal paths keep ?as=&t=). */
  to?: string;
  /** External URL (opens in a new tab unless `download`). */
  href?: string; download?: string | boolean;
  onClick?: (e: MouseEvent) => void;
  icon?: ReactNode; iconEnd?: ReactNode;
  disabled?: boolean; type?: "button" | "submit";
  className?: string; children?: ReactNode;
  "aria-label"?: string; "aria-expanded"?: boolean; "aria-haspopup"?: "menu" | "dialog"; "aria-pressed"?: boolean;
};
/** Button / link-button. `<Btn to={P.session(id)} variant="live" icon={<Play/>}>Join live</Btn>` */
export function Btn({ variant = "primary", size = "md", block, onDark, to, href, download, onClick, icon, iconEnd, disabled, type = "button", className = "", children, ...aria }: BtnProps) {
  const { to: keep } = usePortal();
  const cls = `pt-btn pt-btn-${variant} pt-btn-${size}${block ? " is-block" : ""}${onDark ? " on-dark" : ""}${disabled ? " is-disabled" : ""} ${className}`;
  const inner = <>{icon}{children != null && <span>{children}</span>}{iconEnd}</>;
  if (to && !disabled) return <Link to={keep(to)} className={cls} onClick={onClick} {...aria}>{inner}</Link>;
  if (href && !disabled) return <a href={href} className={cls} onClick={onClick} {...(download ? { download: download === true ? "" : download } : { target: "_blank", rel: "noreferrer" })} {...aria}>{inner}</a>;
  return <button type={type} className={cls} onClick={onClick} disabled={disabled} {...aria}>{inner}</button>;
}

/** Round icon-only button with an accessible label. `<IconBtn label="Close" icon={<X/>} onClick={…} />` */
export function IconBtn({ label, icon, onClick, to, size = 44, className = "" }: { label: string; icon: ReactNode; onClick?: (e: MouseEvent) => void; to?: string; size?: number; className?: string }) {
  const { to: keep } = usePortal();
  const style = { width: size, height: size } as CSSProperties;
  if (to) return <Link to={keep(to)} className={`pt-iconbtn ${className}`} aria-label={label} title={label} style={style}>{icon}</Link>;
  return <button type="button" className={`pt-iconbtn ${className}`} aria-label={label} title={label} onClick={onClick} style={style}>{icon}</button>;
}

export type ChipTone = "neutral" | "teal" | "live" | "gold" | "blush" | "peri" | "ok" | "lock" | "outline";
/** Small status pill. `<Chip tone="gold" icon={<Clock size={14}/>}>Tonight</Chip>` */
export function Chip({ tone = "neutral", icon, children, className = "" }: { tone?: ChipTone; icon?: ReactNode; children: ReactNode; className?: string }) {
  return <span className={`pt-chip pt-chip-${tone} ${className}`}>{tone === "live" && <i className="pt-dot" aria-hidden="true" />}{icon}{children}</span>;
}
const STATE_TONE: Record<SessionState, ChipTone> = { upcoming: "neutral", tonight: "gold", live: "live", processing: "peri", replay: "teal" };
/** Session state chip: Upcoming · Tonight · ● Live now · Replay processing · Replay ready. */
export function SessionChip({ state, className }: { state: SessionState; className?: string }) {
  return <Chip tone={STATE_TONE[state]} className={className}>{STATE_LABEL[state]}</Chip>;
}

export type CardTone = "plain" | "paper" | "teal" | "gold" | "peri" | "blush" | "ink";
/** Surface. Colored tones flip text to the right ink. `to` makes the whole card a link.
    `tape` adds a strip of washi tape at the top (brand accent; use sparingly). */
export function Card({ tone = "plain", pad = "md", to, tape, tapeSide = "center", as: As = "div", className = "", style, children, id }: {
  tone?: CardTone; pad?: "none" | "sm" | "md" | "lg"; to?: string; tape?: TapeColor; tapeSide?: "center" | "left" | "right";
  as?: "div" | "section" | "article" | "li"; className?: string; style?: CSSProperties; children: ReactNode; id?: string;
}) {
  const { to: keep } = usePortal();
  const cls = `pt-card pt-card-${tone} pt-pad-${pad}${to ? " is-link" : ""}${tape ? " has-tape" : ""} ${className}`;
  const body = <>{tape && <Tape color={tape} side={tapeSide} />}{children}</>;
  if (to) return <Link to={keep(to)} className={cls} style={style} id={id}>{body}</Link>;
  return <As className={cls} style={style} id={id}>{body}</As>;
}

/** A card with hairline-separated rows. `<List><Row …/><Row …/></List>`; `flush` drops the card surface. */
export function List({ children, flush, className = "", label }: { children: ReactNode; flush?: boolean; className?: string; label?: string }) {
  return <ul className={`pt-list${flush ? " is-flush" : ""} ${className}`} aria-label={label}>{children}</ul>;
}
/** One scannable list row: [lead] title / meta [trail] [›]. Clickable with to/href/onClick.
    `<Row lead={<Thumb …/>} title="Witch Hat · Prep" meta="52:36 left" to={P.session(id)} />` */
export function Row({ lead, title, meta, trail, to, href, onClick, chevron, className = "", active }: {
  lead?: ReactNode; title: ReactNode; meta?: ReactNode; trail?: ReactNode; to?: string; href?: string;
  onClick?: () => void; chevron?: boolean; className?: string; active?: boolean;
}) {
  const { to: keep } = usePortal();
  const showChev = chevron ?? !!(to || href || onClick);
  const inner = <>
    {lead && <span className="pt-row-lead">{lead}</span>}
    <span className="pt-row-main"><span className="pt-row-title">{title}</span>{meta && <span className="pt-row-meta">{meta}</span>}</span>
    {trail && <span className="pt-row-trail">{trail}</span>}
    {showChev && <ChevronRight className="pt-row-chev" size={18} aria-hidden="true" />}
  </>;
  const cls = `pt-row${active ? " is-active" : ""} ${className}`;
  return <li className="pt-li">
    {to ? <Link to={keep(to)} className={cls}>{inner}</Link>
      : href ? <a href={href} target="_blank" rel="noreferrer" className={cls}>{inner}</a>
      : onClick ? <button type="button" onClick={onClick} className={cls}>{inner}</button>
      : <div className={cls}>{inner}</div>}
  </li>;
}

/** Section header: title, optional kicker / handwritten note, and a right-side action link.
    `<SectionHead title="Continue watching" action={{ label: "All classes", to: P.classes }} />` */
export function SectionHead({ title, kicker, hand, action, as: H = "h2", id }: {
  title: ReactNode; kicker?: ReactNode; hand?: string; action?: { label: string; to: string } | ReactNode; as?: "h2" | "h3"; id?: string;
}) {
  const isLink = !!action && typeof action === "object" && "label" in (action as object) && "to" in (action as object);
  return <div className="pt-sechead">
    <div>
      {kicker && <p className="pt-kicker">{kicker}</p>}
      {hand && <Hand className="pt-sechead-hand">{hand}</Hand>}
      <H className="pt-sechead-title" id={id}>{title}</H>
    </div>
    {action && (isLink ? <PLink className="pt-sechead-action" to={(action as { to: string }).to}>{(action as { label: string }).label}<ChevronRight size={16} aria-hidden="true" /></PLink> : action as ReactNode)}
  </div>;
}
/** SectionHead + content with standard spacing. `<Section title="From Chelsea">…</Section>` */
export function Section({ children, className = "", id, ...head }: Parameters<typeof SectionHead>[0] & { children: ReactNode; className?: string }) {
  const hid = id ? `${id}-h` : undefined;
  return <section className={`pt-section ${className}`} id={id} aria-labelledby={hid}><SectionHead {...head} id={hid} />{children}</section>;
}

/** Segmented control (tabs-like toggle). `<Segmented value={v} onChange={setV} options={[{ value: "pattern", label: "Pattern list" }, …]} />` */
export function Segmented<T extends string>({ value, onChange, options, label, size = "md", block }: {
  value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; count?: number }[]; label?: string; size?: "sm" | "md"; block?: boolean;
}) {
  return <div className={`pt-seg pt-seg-${size}${block ? " is-block" : ""}`} role="tablist" aria-label={label}>
    {options.map(o => <button key={o.value} type="button" role="tab" aria-selected={o.value === value} className={o.value === value ? "is-on" : ""} onClick={() => onChange(o.value)}>
      {o.label}{o.count != null && <span className="pt-seg-count">{o.count}</span>}
    </button>)}
  </div>;
}

/** Avatar from a Person or person id. Chelsea gets her photo; others initials on a palette fill. */
export function Avatar({ person, size = 32, className = "" }: { person: Person | string; size?: number; className?: string }) {
  const p = typeof person === "string" ? who(person) : person;
  const style = { width: size, height: size, fontSize: Math.round(size * 0.4), background: p.color } as CSSProperties;
  return p.avatar
    ? <img className={`pt-avatar ${className}`} src={p.avatar} alt="" style={style} />
    : <span className={`pt-avatar ${className}`} style={style} aria-hidden="true">{p.initials}</span>;
}
/** Name with badges: "Chelsea [Chelsea]" / "Maggie L. [Founding]". */
export function Name({ person, badge = true }: { person: Person | string; badge?: boolean }) {
  const p = typeof person === "string" ? who(person) : person;
  return <span className="pt-name">{p.id === "me" ? `${p.name} (you)` : p.name}
    {badge && p.badge === "chelsea" && <span className="pt-badge pt-badge-chelsea">Chelsea</span>}
    {badge && p.badge === "founding" && <span className="pt-badge">Founding</span>}
  </span>;
}

/** Video/image thumbnail with optional duration, resume bar, play glyph, lock and badge.
    `<Thumb src={s.replay.poster} duration={s.replay.length} progress={s.progress / s.replay.length} play />` */
export function Thumb({ src, alt = "", ratio = "16/9", duration, progress, badge, locked, play, width, className = "" }: {
  src: string; alt?: string; ratio?: string; duration?: number; progress?: number; badge?: ReactNode; locked?: boolean; play?: boolean; width?: number | string; className?: string;
}) {
  return <span className={`pt-thumb ${className}`} style={{ aspectRatio: ratio, width } as CSSProperties}>
    <img src={src} alt={alt} loading="lazy" />
    {badge && <span className="pt-thumb-badge">{badge}</span>}
    {play && !locked && <span className="pt-thumb-play" aria-hidden="true"><Play size={16} fill="currentColor" /></span>}
    {locked && <span className="pt-thumb-lock" aria-label="Locked"><Lock size={14} aria-hidden="true" /></span>}
    {duration != null && <span className="pt-thumb-dur">{fmtDur(duration)}</span>}
    {progress != null && progress > 0 && <span className="pt-thumb-bar" aria-hidden="true"><i style={{ width: `${Math.min(100, progress * 100)}%` }} /></span>}
  </span>;
}

/** Thin progress bar (resume). value 0–1. */
export function ProgressBar({ value, label, className = "" }: { value: number; label?: string; className?: string }) {
  return <span className={`pt-bar ${className}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(value * 100)} aria-label={label}>
    <i style={{ width: `${Math.min(100, Math.max(0, value * 100))}%` }} />
  </span>;
}

/** Progress ring. value 0–1; shows a check when done (≥ 0.98). `showPct` prints the percent inside (size ≥ 40).
    `<ProgressRing value={0.4} size={44} showPct />` */
export function ProgressRing({ value, size = 36, stroke = 4, showPct, label, tone = "teal", className = "" }: {
  value: number; size?: number; stroke?: number; showPct?: boolean; label?: string; tone?: "teal" | "onDark"; className?: string;
}) {
  const v = Math.min(1, Math.max(0, value)), r = (size - stroke) / 2, c = 2 * Math.PI * r, done = v >= 0.98;
  return <span className={`pt-ring pt-ring-${tone}${done ? " is-done" : ""} ${className}`} style={{ width: size, height: size }} role="img" aria-label={label ?? `${Math.round(v * 100)}% watched`}>
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="pt-ring-track" />
      <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} className="pt-ring-val" strokeDasharray={c} strokeDashoffset={c * (1 - v)} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
    {done ? <Check size={Math.round(size * 0.45)} strokeWidth={3} aria-hidden="true" /> : showPct && size >= 40 && <b>{Math.round(v * 100)}%</b>}
  </span>;
}

/** Tap-to-copy discount code. Locked (masked, with a tier chip) for roles without "code" unless gate={false}.
    `<CopyCode code="SPELLBOUND25" discount="25% off" expires={p.pattern.codeExpires} />` */
export function CopyCode({ code, discount, expires, gate = true, block }: { code: string; discount?: string; expires?: Date; gate?: boolean; block?: boolean }) {
  const { role } = usePortal();
  const [copied, setCopied] = useState(false);
  const locked = gate && !can(role, "code");
  const copy = async () => {
    try { await navigator.clipboard.writeText(code); } catch { /* prototype: clipboard may be blocked */ }
    setCopied(true); window.setTimeout(() => setCopied(false), 1800);
  };
  const meta = [discount, expires && `until ${fmtDay(new Date(expires.getTime() - 60e3), DENVER, { month: "short", day: "numeric" })}`].filter(Boolean).join(" · ");
  if (locked) return <div className={`pt-code is-locked${block ? " is-block" : ""}`}>
    <span className="pt-code-val" aria-label="Code hidden">••••••••</span>
    <span className="pt-code-meta">{meta && <span>{meta}</span>}<Chip tone="lock" icon={<Lock size={12} aria-hidden="true" />}>{PLANS.member.tag}</Chip></span>
  </div>;
  return <button type="button" className={`pt-code${copied ? " is-copied" : ""}${block ? " is-block" : ""}`} onClick={copy} aria-label={`Copy code ${code}`}>
    <span className="pt-code-val">{code}</span>
    <span className="pt-code-meta">{meta && <span>{meta}</span>}<span className="pt-code-act" aria-live="polite">{copied ? <><Check size={15} aria-hidden="true" /> Copied</> : <><Copy size={15} aria-hidden="true" /> Copy</>}</span></span>
  </button>;
}

/* ═══════════════════════════ Tiers & locks ═══════════════════════════ */

/** The join/upgrade words for a role reaching for a plan, or null if they already have it.
    ctaFor("friend", "member") → { label: "Upgrade to Member · $25/mo", short: "Upgrade", to: "/blue-2/portal/join?plan=member" } */
export function ctaFor(role: Role, need: Plan): { label: string; short: string; to: string } | null {
  if (can(role, need === "free" ? "notes" : need === "friend" ? "posts" : "live")) return null;
  const to = `${P.join}?plan=${need}`;
  if (need === "free") return { label: "Create a free account", short: "Sign up free", to };
  if (need === "friend") return { label: `Become a Thread Friend · ${PLANS.friend.per}`, short: "Become a Thread Friend", to };
  return role === "friend" || role === "free"
    ? { label: `Upgrade to Member · ${PLANS.member.per}`, short: "Upgrade", to }
    : { label: `Join the club · ${PLANS.member.per}`, short: "Join the club", to };
}
/** Join/upgrade button for the current role; renders nothing when they already have `need`. */
export function JoinButton({ need = "member", short, size = "md", variant = "primary", block, onDark }: { need?: Plan; short?: boolean; size?: BtnSize; variant?: BtnVariant; block?: boolean; onDark?: boolean }) {
  const { role } = usePortal();
  const c = ctaFor(role, need);
  if (!c) return null;
  return <Btn to={c.to} size={size} variant={variant} block={block} onDark={onDark}>{short ? c.short : c.label}</Btn>;
}

const LOCK_COPY: Partial<Record<Thing, string>> = {
  replays: "Every class replay, with chapters and fix notes, comes with the Member plan.",
  chatReplay: "The live chat from class is saved for Members.",
  live: "Live classes are for Members: sew along and ask Chelsea questions as you go.",
  code: "Members get the pattern discount code.",
  qaAsk: "Members can ask Chelsea questions on any class.",
  calendarFeed: "Members get a calendar feed of every class.",
  posts: "Studio posts are for Thread Friends and Members.",
  communityPost: "Thread Friends and Members can post in the community.",
  makesPost: "Thread Friends and Members can share their makes.",
  notes: "Notes from Chelsea are free with an account.",
  supplies: "Supply lists are free with an account.",
  communityRead: "The community is free with an account.",
  freePosts: "This post is free with an account.",
};
/** The tier-named lock line: "Member ($25) · 3h 27m · 30s preview". */
export function lockLine(need: Plan, length?: number, preview?: boolean | number) {
  return [PLANS[need].tag, length ? fmtDur(length) : "", preview ? `${preview === true ? 30 : preview}s preview` : ""].filter(Boolean).join(" · ");
}

/** A tier-named lock. Renders nothing when the role can already open `thing` (pass `force` to always show).
    - variant "card": calm dashed card with the lock line, a sentence and the join/upgrade button
    - variant "inline": one-line chip + short link, for list rows
    - variant "overlay": absolutely fills its (position:relative) parent, for players and thumbnails
    `<TierLock thing="replays" length={s.replay.length} preview />` → "Member ($25) · 3h 4m · 30s preview" */
export function TierLock({ thing = "replays", need, length, preview, title, body, variant = "card", force, onPreview, className = "" }: {
  thing?: Thing; need?: Plan; length?: number; preview?: boolean | number; title?: ReactNode; body?: ReactNode;
  variant?: "card" | "inline" | "overlay"; force?: boolean; onPreview?: () => void; className?: string;
}) {
  const { role } = usePortal();
  if (!force && can(role, thing)) return null;
  const plan: Plan = need ?? (thing === "manage" ? "member" : (["free", "friend", "member"] as Plan[]).find(p => can(p as Role, thing)) ?? "member");
  const line = lockLine(plan, length, preview);
  const cta = ctaFor(role, plan);
  if (variant === "inline") return <span className={`pt-lock-inline ${className}`}>
    <Chip tone="lock" icon={<Lock size={12} aria-hidden="true" />}>{line}</Chip>
    {cta && <PLink to={cta.to} className="pt-lock-link">{cta.short}</PLink>}
  </span>;
  const extra = role === "friend" && plan === "member" ? " You keep everything you have now." : "";
  return <div className={`pt-lock pt-lock-${variant} ${className}`}>
    <div className="pt-lock-inner">
      <span className="pt-lock-icon"><Lock size={18} aria-hidden="true" /></span>
      <p className="pt-lock-line">{line}</p>
      {title && <p className="pt-lock-title">{title}</p>}
      <p className="pt-lock-body">{body ?? `${LOCK_COPY[thing] ?? "This is for members."}${extra}`}</p>
      <div className="pt-lock-actions">
        {cta && <Btn to={cta.to} variant={variant === "overlay" ? "pop" : "primary"}>{cta.label}</Btn>}
        {preview && onPreview && <Btn variant={variant === "overlay" ? "secondary" : "ghost"} onDark={variant === "overlay"} icon={<Play size={16} aria-hidden="true" />} onClick={onPreview}>Watch 30s preview</Btn>}
        {role === "visitor" && <PLink to={P.me} className="pt-lock-link">Already a member? Sign in</PLink>}
      </div>
    </div>
  </div>;
}
/** Renders children when the role can open `thing`, else a TierLock (props in `lock`).
    `<Gate thing="code" lock={{ variant: "inline" }}><CopyCode … /></Gate>` */
export function Gate({ thing, children, lock }: { thing: Thing; children: ReactNode; lock?: Omit<Parameters<typeof TierLock>[0], "thing"> }) {
  const { role } = usePortal();
  return can(role, thing) ? <>{children}</> : <TierLock thing={thing} {...lock} />;
}

/* ═══════════════════════════ Brand accents ═══════════════════════════ */

export type TapeColor = "pink" | "yellow" | "green" | "peri";
/** A strip of washi tape (absolute; parent needs position:relative — Card handles this). */
export function Tape({ color = "yellow", side = "center", className = "" }: { color?: TapeColor; side?: "center" | "left" | "right"; className?: string }) {
  return <span className={`pt-tape pt-tape-${color} pt-tape-${side} ${className}`} aria-hidden="true" />;
}
/** Handwritten note (Gochi Hand). `<Hand>see you at 7!</Hand>` */
export function Hand({ children, className = "", as: As = "p" }: { children: ReactNode; className?: string; as?: "p" | "span" }) {
  return <As className={`pt-hand ${className}`}>{children}</As>;
}
/** Taped polaroid for makes. `<Polaroid src={m.img} caption={m.caption} tilt={-2} tape="pink" />` */
export function Polaroid({ src, alt = "", caption, by, tilt = 0, tape, to, className = "" }: {
  src: string; alt?: string; caption?: ReactNode; by?: ReactNode; tilt?: number; tape?: TapeColor; to?: string; className?: string;
}) {
  const { to: keep } = usePortal();
  const body = <>
    {tape && <Tape color={tape} />}
    <span className="pt-polaroid-img"><img src={src} alt={alt} loading="lazy" /></span>
    {(caption || by) && <span className="pt-polaroid-cap">{caption && <span className="pt-polaroid-text">{caption}</span>}{by && <span className="pt-polaroid-by">{by}</span>}</span>}
  </>;
  const style = { "--tilt": `${tilt}deg` } as CSSProperties;
  return to ? <Link to={keep(to)} className={`pt-polaroid ${className}`} style={style}>{body}</Link> : <figure className={`pt-polaroid ${className}`} style={style}>{body}</figure>;
}
/** Empty state with an optional handwritten aside. `<Empty title="No makes yet" hand="be the first!" action={<Btn …/>} />` */
export function Empty({ icon, title, body, hand, action, className = "" }: { icon?: ReactNode; title: ReactNode; body?: ReactNode; hand?: string; action?: ReactNode; className?: string }) {
  return <div className={`pt-empty ${className}`}>
    {icon && <span className="pt-empty-icon">{icon}</span>}
    {hand && <Hand className="pt-empty-hand">{hand}</Hand>}
    <p className="pt-empty-title">{title}</p>
    {body && <p className="pt-empty-body">{body}</p>}
    {action && <div className="pt-empty-action">{action}</div>}
  </div>;
}

/* ═══════════════════════════ Sheet ═══════════════════════════ */

/** Bottom sheet on phones, right-hand drawer from 900px. Escape / backdrop closes.
    `<Sheet open={o} onClose={() => setO(false)} title="What you need">…</Sheet>` */
export function Sheet({ open, onClose, title, children, footer }: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; footer?: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", esc);
    const prev = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", esc); document.body.style.overflow = prev; };
  }, [open, onClose]);
  if (!open) return null;
  return <div className="pt-sheet-wrap" onClick={onClose}>
    <div className="pt-sheet" role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : undefined} onClick={e => e.stopPropagation()}>
      <span className="pt-sheet-grip" aria-hidden="true" />
      <div className="pt-sheet-head"><h2>{title}</h2><IconBtn label="Close" icon={<X size={20} />} onClick={onClose} /></div>
      <div className="pt-sheet-body">{children}</div>
      {footer && <div className="pt-sheet-foot">{footer}</div>}
    </div>
  </div>;
}

/* ═══════════════════════════ Supply checklist (per member) ═══════════════════════════ */

/** The member's supply checklist for a project, kept in localStorage (seeded from MY_SUPPLIES).
    `const ck = useChecklist("witch-hat"); ck.has(id); ck.toggle(id); ck.count(items)` */
export function useChecklist(projectId: string) {
  const key = `pt-supplies-${projectId}`;
  const [have, setHave] = useState<Set<string>>(() => {
    try { const raw = localStorage.getItem(key); if (raw) return new Set(JSON.parse(raw) as string[]); } catch { /* storage blocked */ }
    return new Set(MY_SUPPLIES[projectId] ?? []);
  });
  useEffect(() => {
    const sync = (e: Event) => { const d = (e as CustomEvent).detail as { key: string; ids: string[] }; if (d.key === key) setHave(new Set(d.ids)); };
    window.addEventListener("pt-checklist", sync);
    return () => window.removeEventListener("pt-checklist", sync);
  }, [key]);
  const toggle = (id: string) => setHave(prev => {
    const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id);
    const ids = [...n];
    try { localStorage.setItem(key, JSON.stringify(ids)); } catch { /* storage blocked */ }
    window.setTimeout(() => window.dispatchEvent(new CustomEvent("pt-checklist", { detail: { key, ids } })), 0);
    return n;
  });
  return {
    have, has: (id: string) => have.has(id), toggle,
    /** "6 of 9" counts for a list (optional items excluded from the total unless ticked). */
    count: (items: { id: string; optional?: boolean }[]) => {
      const req = items.filter(i => !i.optional);
      return { done: req.filter(i => have.has(i.id)).length, total: req.length };
    },
  };
}

/* ═══════════════════════════ Live banner & pinned note ═══════════════════════════ */

/** The red LIVE NOW bar. Auto-detects a live session on the simulated clock; renders nothing otherwise.
    Shown by PortalShell on every screen (hideLive to suppress, e.g. on that session's own page). */
export function LiveBanner({ session: forced }: { session?: Session }) {
  const now = useNow(15000);
  const { role } = usePortal();
  const s = forced ?? liveSession(now);
  if (!s) return null;
  if (s.kind === "youtube") return <a className="pt-live pt-live-yt" href={s.url} target="_blank" rel="noreferrer">
    <span className="pt-live-tag"><i className="pt-dot" aria-hidden="true" />Live on YouTube</span>
    <span className="pt-live-title">{s.title}</span>
    <span className="pt-live-cta">Watch free <ExternalLink size={14} aria-hidden="true" /></span>
  </a>;
  const ok = can(role, "live");
  return <PLink className="pt-live" to={ok ? P.session(s.id) : `${P.join}?plan=member`}>
    <span className="pt-live-tag"><i className="pt-dot" aria-hidden="true" />Live now</span>
    <span className="pt-live-title">{s.title}</span>
    <span className="pt-live-cta">{ok ? "Join" : "Members · Join the club"} <ChevronRight size={16} aria-hidden="true" /></span>
  </PLink>;
}

const dismissed = new Set<string>();
/** A pinned note from Chelsea (defaults to the one pinned at the simulated now). Dismissible for this visit.
    Free accounts and up see notes; visitors see only notes with need "visitor". */
export function PinnedNote({ note: forced, dismissible = true }: { note?: Note; dismissible?: boolean }) {
  const { role, now } = usePortal();
  const [, force] = useState(0);
  const n = forced ?? pinnedNote(now);
  if (!n || dismissed.has(n.id)) return null;
  if (!can(role, "notes") && n.need !== "visitor") return null;
  return <div className="pt-pin" role="status">
    <Avatar person="chelsea" size={32} />
    <div className="pt-pin-main">
      <p className="pt-pin-meta"><Pin size={13} aria-hidden="true" /> Pinned · Chelsea · {ago(n.at, now)}</p>
      <p className="pt-pin-text">{n.text}</p>
    </div>
    {dismissible && <IconBtn label="Dismiss note" icon={<X size={18} />} onClick={() => { dismissed.add(n.id); force(x => x + 1); }} size={40} />}
  </div>;
}

/* ═══════════════════════════ Review controls ═══════════════════════════ */

/** Fixed prototype controls: who's viewing (?as=) and the simulated clock (?t=). Rendered by PortalShell. */
export function ReviewControls() {
  const { role, clock, setRole, setClock } = usePortal();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", esc);
    return () => document.removeEventListener("keydown", esc);
  }, [open]);
  const r = ROLES.find(x => x.key === role)!, c = CLOCKS.find(x => x.key === clock)!;
  return <div className={`pt-review${open ? " is-open" : ""}`}>
    {open && <div className="pt-review-panel" role="dialog" aria-label="Prototype review controls">
      <div className="pt-review-head"><b>Prototype review</b><button type="button" onClick={() => setOpen(false)} aria-label="Close review controls"><X size={16} /></button></div>
      <p className="pt-review-label">Viewing as</p>
      <div className="pt-review-opts" role="radiogroup" aria-label="Viewing as">
        {ROLES.map(x => <button key={x.key} type="button" role="radio" aria-checked={x.key === role} className={x.key === role ? "is-on" : ""} onClick={() => setRole(x.key)} title={x.note}>{x.short}</button>)}
      </div>
      <p className="pt-review-note">{r.note}</p>
      <p className="pt-review-label">Clock</p>
      <div className="pt-review-opts" role="radiogroup" aria-label="Simulated time">
        {CLOCKS.map(x => <button key={x.key} type="button" role="radio" aria-checked={x.key === clock} className={x.key === clock ? "is-on" : ""} onClick={() => setClock(x.key)}>{x.label}</button>)}
      </div>
      <p className="pt-review-note">Simulated now: {c.note}. Sample data only; nothing is saved or sent.</p>
    </div>}
    <button type="button" className="pt-review-pill" onClick={() => setOpen(o => !o)} aria-expanded={open}>
      <SlidersHorizontal size={14} aria-hidden="true" /><span>{r.short}</span><span className="pt-review-sep" aria-hidden="true">·</span><span>{c.label}</span>
    </button>
  </div>;
}

/* ═══════════════════════════ Shell ═══════════════════════════ */

const TABS: { key: Tab; label: string; to: string; icon: (on: boolean) => ReactNode }[] = [
  { key: "home", label: "Home", to: P.home, icon: () => <House size={22} aria-hidden="true" /> },
  { key: "classes", label: "Classes", to: P.classes, icon: () => <Clapperboard size={22} aria-hidden="true" /> },
  { key: "community", label: "Community", to: P.community, icon: () => <MessagesSquare size={22} aria-hidden="true" /> },
];

/** "Loose Threads club" wordmark. */
export function Wordmark({ size = "md" }: { size?: "sm" | "md" }) {
  return <PLink to={P.home} className={`pt-wordmark pt-wordmark-${size}`} aria-label="Loose Threads Club home">
    <span className="pt-wordmark-main">Loose Threads</span><span className="pt-wordmark-club">club</span>
  </PLink>;
}

function MeAvatar({ size = 28 }: { size?: number }) {
  const { role } = usePortal();
  if (role === "visitor") return <UserRound size={size - 4} aria-hidden="true" />;
  return <Avatar person={role === "chelsea" ? "chelsea" : ME} size={size} />;
}

function PlanBox() {
  const { role, now, tz } = usePortal();
  const next = heroSession(now);
  const s = next?.session;
  const proj = s && projectOf(s);
  return <div className="pt-side-extra">
    {s && <PLink className="pt-side-next" to={P.session(s.id)}>
      <span className="pt-side-next-k">{next!.state === "live" ? <Chip tone="live">Live now</Chip> : next!.state === "replay" ? "Replay ready" : next!.state === "processing" ? "Replay processing" : "Next class"}</span>
      <span className="pt-side-next-t">{proj?.title ?? s.title} · {s.label}</span>
      {next!.state !== "replay" && next!.state !== "processing" && <span className="pt-side-next-m">{relDay(s.start, now, tz)} · {timeLabel(s.start, tz, "yours")}</span>}
    </PLink>}
    {role === "member" && <p className="pt-side-plan"><Chip tone="teal">{PLANS.member.name} · {PLANS.member.per}</Chip></p>}
    {role === "chelsea" && <p className="pt-side-plan"><Chip tone="peri">Creator view</Chip></p>}
    {role === "friend" && <div className="pt-side-plan"><Chip tone="blush">{PLANS.friend.name} · {PLANS.friend.per}</Chip><JoinButton need="member" short size="sm" block /></div>}
    {role === "free" && <div className="pt-side-plan"><Chip>Free account</Chip><JoinButton need="member" size="sm" block /></div>}
    {role === "visitor" && <div className="pt-side-plan"><JoinButton need="member" size="sm" block /><PLink to={P.me} className="pt-side-signin">Sign in</PLink></div>}
  </div>;
}

export type PortalShellProps = {
  /** Which destination is highlighted (null for screens outside the 4, e.g. Join). */
  active?: Tab | null;
  /** Page title (compact; Fraunces). Omit on Home, where the hero card leads. */
  title?: ReactNode;
  /** Small line under the title. */
  subtitle?: ReactNode;
  /** Kicker above the title (e.g. "October 2026"). */
  kicker?: ReactNode;
  /** Back link for sub-pages (project, session): shown in the phone top bar and above the desktop title. */
  back?: { to: string; label: string };
  /** Right side of the page header (buttons, a search field…). */
  actions?: ReactNode;
  /** Desktop: sticky right rail (~320px). Phone: rendered per railPhone. */
  rail?: ReactNode;
  /** Where the rail goes on phones: after the content (default), before it, or hidden. */
  railPhone?: "after" | "before" | "hide";
  /** Pinned-note strip at the top of the page: "auto" = the note pinned now (default on Home), a node, or false. */
  pinned?: ReactNode | "auto" | false;
  /** Hide the LIVE NOW banner (e.g. on the live session's own page). */
  hideLive?: boolean;
  /** Focus mode for the session/player screen: hides the phone tab bar and drops the page padding/max-width. */
  focus?: boolean;
  /** Let the content use the full width (no 1200px cap). */
  wide?: boolean;
  className?: string;
  children: ReactNode;
};

/** The portal frame. Every portal screen renders inside it.
    `<PortalShell active="classes" title="Classes" actions={<Search/>}>…</PortalShell>`
    `<PortalShell active="home" rail={<NextClassCard/>}>…</PortalShell>` */
export function PortalShell({ active = null, title, subtitle, kicker, back, actions, rail, railPhone = "after", pinned, hideLive, focus, wide, className = "", children }: PortalShellProps) {
  useFonts();
  const { role, now } = usePortal();
  const live = !hideLive && !!liveSession(now);
  useEffect(() => { document.title = `${typeof title === "string" ? `${title} · ` : ""}Loose Threads Club`; }, [title]);
  const pin = pinned === undefined ? (active === "home" ? "auto" : false) : pinned;
  const meLabel = role === "visitor" ? "Sign in" : "Me";
  return <div className={`pt${focus ? " is-focus" : ""}${live ? " has-live" : ""} ${className}`} style={rootStyle}>
    {/* Desktop sidebar */}
    <aside className="pt-side" aria-label="Club">
      <Wordmark />
      <nav className="pt-side-nav" aria-label="Club sections">
        {TABS.map(t => <PLink key={t.key} to={t.to} className={`pt-side-link${active === t.key ? " is-on" : ""}`} aria-current={active === t.key ? "page" : undefined}>{t.icon(active === t.key)}<span>{t.label}</span></PLink>)}
      </nav>
      <PlanBox />
      <PLink to={P.me} className={`pt-side-me${active === "me" ? " is-on" : ""}`} aria-current={active === "me" ? "page" : undefined}>
        <MeAvatar size={32} />
        <span><b>{role === "visitor" ? "Sign in" : role === "chelsea" ? "Chelsea" : ME.name}</b><small>{role === "visitor" ? "or create an account" : "Me · settings & membership"}</small></span>
      </PLink>
    </aside>

    <div className="pt-main">
      <div className="pt-stick">
        {/* Phone top bar */}
        <header className="pt-top">
          {back
            ? <PLink to={back.to} className="pt-top-back"><ChevronLeft size={22} aria-hidden="true" /><span>{back.label}</span></PLink>
            : <Wordmark size="sm" />}
          <div className="pt-top-right">
            {(role === "visitor" || role === "free") && !focus && <JoinButton need="member" short size="sm" />}
            {role !== "visitor" && <PLink to={P.me} className="pt-top-me" aria-label="Me">{<MeAvatar size={30} />}</PLink>}
          </div>
        </header>
        {!hideLive && <LiveBanner />}
      </div>

      <div className={`pt-page${wide ? " is-wide" : ""}`}>
        {pin === "auto" ? <PinnedNote /> : pin || null}
        {(title || actions || (back && !focus)) && <header className="pt-pagehead">
          <div className="pt-pagehead-main">
            {back && <PLink to={back.to} className="pt-pagehead-back"><ChevronLeft size={16} aria-hidden="true" />{back.label}</PLink>}
            {kicker && <p className="pt-kicker">{kicker}</p>}
            {title && <h1 className="pt-h1">{title}</h1>}
            {subtitle && <p className="pt-pagehead-sub">{subtitle}</p>}
          </div>
          {actions && <div className="pt-pagehead-actions">{actions}</div>}
        </header>}
        {rail
          ? <div className={`pt-grid pt-rail-${railPhone}`}>
              <div className="pt-content">{children}</div>
              <aside className="pt-rail" aria-label="Sidebar">{rail}</aside>
            </div>
          : <div className="pt-content">{children}</div>}
      </div>
    </div>

    {/* Phone bottom tabs */}
    <nav className="pt-tabs" aria-label="Club sections">
      {TABS.map(t => <PLink key={t.key} to={t.to} className={`pt-tab${active === t.key ? " is-on" : ""}`} aria-current={active === t.key ? "page" : undefined}>
        <span className="pt-tab-icon">{t.icon(active === t.key)}</span><span className="pt-tab-label">{t.label}</span>
      </PLink>)}
      <PLink to={P.me} className={`pt-tab${active === "me" ? " is-on" : ""}`} aria-current={active === "me" ? "page" : undefined}>
        <span className="pt-tab-icon"><MeAvatar size={24} /></span><span className="pt-tab-label">{meLabel}</span>
      </PLink>
    </nav>

    <ReviewControls />
  </div>;
}

/* Re-export the data layer so screens can import everything from one place. */
export * from "./data";
